"use client";

import { motion } from "framer-motion";
import { Handshake, Bot, Check, ArrowRight } from "lucide-react";
import { Container } from "@/components/ui/container";
import { Section } from "@/components/ui/section";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { productStats } from "@/lib/constants";

const apps = [
  {
    key: "buddify",
    name: "Brand Partnerships",
    description: `Connect with ${productStats.brandPartnerships.brands} independent brands for partner dropshipping, wholesale B2B, and marketplace listings.`,
    href: "/brand-partnerships/pricing",
    installHref: "https://apps.shopify.com/buddify",
    icon: Handshake,
    stats: [
      { label: "Brands", value: productStats.brandPartnerships.brands },
      {
        label: "Partnerships",
        value: productStats.brandPartnerships.partnerships,
      },
      { label: "Rating", value: `${productStats.brandPartnerships.rating}/5` },
    ],
    features: [
      "Partner dropshipping & wholesale",
      "Marketplace sales channel",
      "Auto order sync & tracking",
      "No transaction fees",
    ],
    theme: "light" as const,
  },
  {
    key: "ai-assistant",
    name: "Buddy AI",
    description:
      "Your 24/7 AI Sales, Support & Conversion team. Turn visitors into buyers with smart conversations.",
    href: "/buddy-ai-assistant/pricing",
    installHref: "https://apps.shopify.com/buddify-ai-discount-negotiator",
    icon: Bot,
    stats: [
      {
        label: "Conversions",
        value: `+${productStats.aiAssistant.conversionIncrease}`,
      },
      { label: "AOV", value: `+${productStats.aiAssistant.aovIncrease}` },
      { label: "Rating", value: `${productStats.aiAssistant.rating}/5` },
    ],
    features: [
      "AI sales & support agent",
      "AI conversion agent",
      "Lead capture & product Q&A",
      "Auto-trained on your store",
    ],
    theme: "dark" as const,
  },
];

const themeStyles = {
  light: {
    card: "bg-panel border-line hover:border-brand-green-line hover:shadow-primary-500/10",
    iconBg: "bg-brand-green-soft text-brand-green",
    check: "text-brand-green",
    button: "primary" as const,
  },
  dark: {
    card: "bg-panel border-line hover:border-brand-violet-line hover:shadow-stone-900/30",
    iconBg: "bg-brand-violet-soft text-brand-violet",
    check: "text-brand-violet",
    button: "ai" as const,
  },
};

export function PricingHubSection() {
  return (
    <Section background="gray" size="sm">
      <Container>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-h1 text-ink mb-4">Pricing</h1>
          <p className="text-body-lg text-muted max-w-2xl mx-auto">
            Start free on either product. Scale as your business grows.
          </p>
        </motion.div>

        {/* App Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {apps.map((app, index) => {
            const theme = themeStyles[app.theme];
            const Icon = app.icon;

            return (
              <motion.div
                key={app.key}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
                className={cn(
                  "group relative rounded-2xl border p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl",
                  theme.card,
                )}
              >
                {/* Icon */}
                <div
                  className={cn(
                    "inline-flex items-center justify-center w-14 h-14 rounded-xl mb-6 transition-transform duration-300 group-hover:scale-110",
                    theme.iconBg,
                  )}
                >
                  <Icon className="w-7 h-7" />
                </div>

                {/* Title */}
                <h2
                  className={cn(
                    "text-h3 mb-3",
                    app.theme === "dark" ? "text-ink" : "text-ink",
                  )}
                >
                  {app.name}
                </h2>
                <p
                  className={cn(
                    "text-body-md mb-6",
                    app.theme === "dark" ? "text-muted" : "text-muted",
                  )}
                >
                  {app.description}
                </p>

                {/* Stats */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {app.stats.map((stat) => (
                    <div
                      key={stat.label}
                      className={cn(
                        "px-3 py-1.5 rounded-lg",
                        app.theme === "dark" ? "bg-brand-violet-soft" : "bg-brand-green-soft",
                      )}
                    >
                      <span
                        className={cn(
                          "text-sm font-semibold",
                          app.theme === "dark"
                            ? "text-brand-violet"
                            : "text-brand-green",
                        )}
                      >
                        {stat.value}
                      </span>
                      <span
                        className={cn(
                          "text-xs ml-1.5",
                          app.theme === "dark"
                            ? "text-muted"
                            : "text-muted",
                        )}
                      >
                        {stat.label}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {app.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <Check
                        className={cn(
                          "w-5 h-5 mt-0.5 flex-shrink-0",
                          theme.check,
                        )}
                      />
                      <span
                        className={cn(
                          app.theme === "dark"
                            ? "text-muted"
                            : "text-muted",
                        )}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTAs */}
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    href={app.href}
                    variant={theme.button}
                    size="lg"
                    className="flex-1"
                  >
                    View pricing
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                  <Button
                    href={app.installHref}
                    external
                    variant={app.theme === "dark" ? "secondary" : "secondary"}
                    size="lg"
                    className={cn(
                      "flex-1",
                      app.theme === "dark" &&
                        "bg-raised text-ink border-line-strong hover:bg-raised hover:text-ink",
                    )}
                  >
                    Install on Shopify
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Cross-sell note */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="text-center text-sm text-muted mt-10"
        >
          Use both together? Many merchants start with Brand Partnerships to
          expand their catalog, then add Buddy AI to convert more visitors.
        </motion.p>
      </Container>
    </Section>
  );
}
